// Pure helpers of the Journal tab (audit log, GET /api/audit): French action labels, tones,
// compact details, filters and facets. Tested in test/ui-admin.test.js.
import { t } from '../../i18n/fr.js';
import { policySummary } from '../connectorpolicy.js';
import './strings.js';

/** Exact labels for the actions the server writes today. */
const EXACT = {
  'auth.login': 'Connexion',
  'auth.login_failed': 'Connexion refusée',
  'auth.logout': 'Déconnexion',
  'auth.setup': 'Création du premier administrateur',
  'auth.password': 'Mot de passe changé',
  'auth.session_revoked': 'Session révoquée',
  'user.create': 'Utilisateur créé',
  'user.update': 'Utilisateur modifié',
  'user.delete': 'Utilisateur supprimé',
  'user.reset_password': 'Mot de passe réinitialisé',
  'connectors.refresh': 'Connecteurs actualisés',
  'connectors.override': 'Classement d’un outil modifié',
  'engines.refresh': 'Moteurs vérifiés',
  'engines.login_started': 'Connexion d’un compte IA démarrée',
  'engines.login_succeeded': 'Compte IA connecté',
  'engines.login_failed': 'Connexion d’un compte IA échouée',
  'engines.logout': 'Compte IA déconnecté',
  'engines.gemini_key_set': 'Clé Gemini enregistrée',
  'engines.gemini_key_removed': 'Clé Gemini supprimée',
  'engines.kie_key_set': 'Clé Kie.ai enregistrée',
  'engines.kie_key_removed': 'Clé Kie.ai supprimée',
  // The office's MCP server (docs/wip/SPEC-MCP.md): access keys (admin or personal: `kind`, and
  // `for` / `forName` the member of a personal key, `reason` when the server revoked it), calls
  // made by Claude with them (tool name as target, the acting person as user, key name, kind and
  // success in details; never the arguments), Tables du bureau.
  'mcp.key_created': 'Clé d’accès Claude (MCP) créée',
  'mcp.key_revoked': 'Clé d’accès Claude (MCP) révoquée',
  'mcp.call': 'Appel de Claude (MCP)',
  'tables.create': 'Table créée',
  'tables.insert': 'Lignes ajoutées à une table',
  'tables.update': 'Lignes modifiées dans une table',
  'tables.delete': 'Lignes supprimées d’une table',
  'tables.drop': 'Table supprimée',
  // A whole department moved to another floor (its agents, routines, triggers, tasks, grants).
  'dept.move': 'Service changé d’étage',
  'office.seed': 'Bureau initialisé (organisation de départ)',
  'office.reseed': 'Organisation remplacée par le modèle fourni',
  'office.export': 'Structure exportée',
  'office.import': 'Structure importée',
  // Suggestions (SPEC §23): the title in details, never the text; progress also comes from Claude (MCP).
  'suggestion.create': 'Suggestion proposée',
  'suggestion.update': 'Suggestion modifiée',
  'suggestion.delete': 'Suggestion supprimée',
  'suggestion.approve': 'Suggestion approuvée',
  'suggestion.reject': 'Suggestion refusée',
  'suggestion.progress': 'Avancement d’une suggestion',
  // The office's connector library (connectors added in the office): the connector id, its host
  // and name in details (`from` the former name of a rename, `autoAllowed` when the member's own
  // addition joined his « Connecteurs autorisés »), never the address of the login page nor the
  // pasted callback address.
  'connector.library.add': 'Connecteur ajouté au bureau',
  'connector.library.rename': 'Connecteur du bureau renommé',
  'connector.library.remove': 'Connecteur retiré du bureau',
  'connector.library.login_start': 'Connexion d’un connecteur du bureau démarrée',
  'connector.library.login_ok': 'Connecteur du bureau connecté',
  'connector.library.login_fail': 'Connexion d’un connecteur du bureau échouée',
  // Plugin settings changed (`fields` names them, never a key or a token), the office's Shopify
  // app (Client ID, scopes, API version, whether the secret changed), and a tool of a connector
  // called by Claude through the admin MCP connector (tool, kind, success, access key name).
  'connector.library.update': 'Réglages d’un connecteur du bureau modifiés',
  'connector.library.shopify_app': 'Application Shopify du bureau modifiée',
  'connector.library.call': 'Outil d’un connecteur du bureau utilisé par Claude (MCP)',
  // Former personal connectors (before the library), kept to read the older entries.
  'connector.personal.add': 'Connecteur personnel ajouté',
  'connector.personal.rename': 'Connecteur personnel renommé',
  'connector.personal.remove': 'Connecteur personnel supprimé',
  'connector.personal.login_start': 'Connexion d’un connecteur personnel démarrée',
  'connector.personal.login_ok': 'Connecteur personnel connecté',
  'connector.personal.login_fail': 'Connexion d’un connecteur personnel échouée',
  'connector.personal.cut': 'Connecteur personnel coupé par l’administrateur',
  // Google Drive (server/modules/drive.js): the office's Google app (Client ID, which settings
  // changed, the Google Drive connectors disconnected by another client or a removal), a Google
  // Drive connector of the library (`connector`): its connection (its access, its Google account;
  // `reason` of a failure), its folder, the storage of the office's files, its pause. Never a token.
  'drive.app': 'Application Google du bureau modifiée',
  'drive.login_start': 'Connexion d’un Google Drive démarrée',
  'drive.login_ok': 'Google Drive connecté',
  'drive.login_fail': 'Connexion d’un Google Drive échouée',
  'drive.disconnect': 'Google Drive déconnecté',
  'drive.folder': 'Dossier d’un Google Drive choisi',
  'drive.storage': 'Stockage des fichiers du bureau dans Google Drive modifié',
  'drive.pause': 'Transferts vers un Google Drive mis en pause ou repris',
  // What members do day to day (also listed in Administration → Activité): their requests and
  // their decisions on the tasks, their messages to the agents (metadata only, never the text),
  // the floor notes, the routines (`routine.run` keeps its generic label), the Studio.
  'task.create': 'Tâche demandée',
  'task.approve': 'Brouillon approuvé',
  'task.reject': 'Brouillon refusé',
  'task.cancel': 'Tâche annulée',
  'task.retry': 'Tâche relancée',
  'task.ignore': 'Problème ignoré',
  'task.unignore': 'Problème de nouveau suivi',
  'task.decide': 'Décision prise sur une tâche',
  'task.delete': 'Tâche supprimée',
  'chat.send': 'Message envoyé à un agent',
  'chat.clear': 'Discussion effacée',
  'note.write': 'Note enregistrée',
  'note.delete': 'Note supprimée',
  'routine.create': 'Routine créée',
  'routine.update': 'Routine modifiée',
  'routine.delete': 'Routine supprimée',
  'studio.create': 'Élément du Studio créé',
  'studio.update': 'Élément du Studio modifié',
  'studio.attach': 'Fichiers joints dans le Studio',
  'studio.file.delete': 'Fichier du Studio supprimé',
  'studio.delete': 'Élément du Studio supprimé',
  'studio.regenerate': 'Régénération demandée dans le Studio',
  'studio.version': 'Version choisie dans le Studio',
  'studio.force': 'Création forcée dans le Studio',
  // An administrator read a member's discussion (Administration → Activité, OFFICE_ADMIN_CHAT_VIEW).
  'activity.chat.read': 'Discussion d’un membre lue par un administrateur',
};

/** Domains (first segment of the action). */
const DOMAINS = {
  auth: 'Connexion', user: 'Utilisateur', users: 'Utilisateur', floor: 'Étage', floors: 'Étage',
  dept: 'Service', department: 'Service', departments: 'Service', agent: 'Agent', agents: 'Agent',
  lesson: 'Règle apprise', lessons: 'Règle apprise', task: 'Tâche', tasks: 'Tâche', routine: 'Routine',
  routines: 'Routine', trigger: 'Déclencheur', triggers: 'Déclencheur', hook: 'Déclencheur', hooks: 'Déclencheur',
  callback: 'Retour n8n', connector: 'Connecteurs', connectors: 'Connecteurs', engine: 'Moteurs',
  engines: 'Moteurs', atelier: 'Atelier', note: 'Note', notes: 'Notes', chat: 'Discussion',
  settings: 'Réglages', office: 'Bureau', calendar: 'Calendrier', deploy: 'Déploiement', usage: 'Consommation',
  mcp: 'Accès Claude (MCP)', table: 'Tables', tables: 'Tables', drive: 'Google Drive', studio: 'Studio',
  activity: 'Activité', file: 'Fichier',
};

/** Verbs (last segment), as nouns so every label reads "Domaine · nom". */
const VERBS = {
  create: 'création', created: 'création', add: 'ajout', update: 'modification', updated: 'modification',
  edit: 'modification', patch: 'modification', delete: 'suppression', deleted: 'suppression', remove: 'suppression',
  reset_password: 'mot de passe réinitialisé', refresh: 'actualisation', override: 'classement modifié',
  run: 'lancement', run_now: 'lancement immédiat', runnow: 'lancement immédiat', fire: 'déclenchement',
  fired: 'déclenchement', approve: 'approbation', approved: 'approbation', reject: 'refus', rejected: 'refus',
  cancel: 'annulation', cancelled: 'annulation', retry: 'relance', rotate: 'nouveau secret',
  rotate_secret: 'nouveau secret', test: 'test', tests: 'tests', apply: 'application', applied: 'application',
  discard: 'abandon', discarded: 'abandon', start: 'démarrage', message: 'message', follow_up: 'message',
  write: 'écriture', put: 'écriture', clear: 'effacement', pause: 'mise en pause', resume: 'reprise',
  enable: 'activation', disable: 'désactivation', login: 'connexion', logout: 'déconnexion', grant: 'accès donné',
  revoke: 'accès retiré', move: 'déplacement', reorder: 'réorganisation', seed: 'installation initiale',
  lesson_add: 'règle ajoutée', lesson_remove: 'règle retirée', send: 'envoi', failed: 'échec', error: 'erreur',
  insert: 'ajout', drop: 'suppression', call: 'appel', attach: 'fichiers joints', regenerate: 'régénération',
  version: 'version choisie', force: 'création forcée', ignore: 'ignoré', unignore: 'de nouveau suivi',
  decide: 'décision', read: 'lecture',
};

const DANGER_RE = /(delete|remove|discard|reject|fail|error|revoke|disable|drop|\bcut$)/i;
const AMBER_RE = /(reset|override|rotate|force|password|cancel|pause)/i;
const SUCCESS_RE = /(create|apply|approve|setup|enable|add|resume)/i;
// Defense in depth: the server already strips these keys from details (modules/audit.js).
const SECRET_KEY_RE = /(password|passwd|secret|token|cookie|csrf|apikey|api_key|authorization|code_verifier|redirect_?url)(hash)?$/i;

/** Labels of common detail keys. */
const DETAIL_KEYS = {
  username: 'identifiant', role: 'rôle', grants: 'accès', fields: 'champs', ip: 'IP', kind: 'classement',
  servers: 'serveurs', error: 'erreur', session: 'session', name: 'nom', title: 'titre', state: 'état',
  floorId: 'étage', deptId: 'service', agentId: 'agent', taskId: 'tâche', force: 'forcé', files: 'fichiers',
  count: 'nombre', reason: 'raison', level: 'niveau', engine: 'moteur', model: 'modèle',
  enabled: 'activé', paused: 'en pause', autonomy: 'validation', callbackUrl: 'rappel', externalRef: 'réf. externe',
  outputFormat: 'format', chars: 'caractères', async: 'asynchrone',
  key: 'clé', hint: 'indice', ok: 'réussi', table: 'table', rows: 'lignes', inserted: 'ajoutées',
  replaced: 'remplacées', skipped: 'ignorées', columns: 'colonnes', keyColumn: 'colonne clé',
  connector: 'connecteur', host: 'adresse', personalConnectors: 'peut ajouter des connecteurs',
  canAddConnectors: 'peut ajouter des connecteurs', connectorPolicy: 'connecteurs autorisés', for: 'compte', forName: 'pour',
  label: 'nom', from: 'ancien nom', autoAllowed: 'autorisé pour lui', tool: 'outil', via: 'clé d’accès',
  shop: 'boutique', clientId: 'Client ID', scopes: 'scopes', apiVersion: 'version de l’API',
  secretChanged: 'secret changé', cleared: 'effacé',
  apiKeyChanged: 'clé API changée', appIdChanged: 'numéro du projet changé', connectorsCleared: 'Google Drive déconnectés',
  mode: 'accès', account: 'compte Google', previousAccount: 'ancien compte', folderId: 'dossier',
  storage: 'stocke les fichiers du bureau', previous: 'stockage précédent',
  // Tasks, notes and the Studio (task.approve / reject / retry / decide, note.write, studio.*).
  edited: 'corrigé', next: 'état suivant', rerun: 'relancée', remember: 'règle retenue', followUp: 'suite',
  accepted: 'acceptées', refused: 'refusées', comment: 'commentaire', routineId: 'routine', attempt: 'essai',
  size: 'taille', created: 'nouvelle', briefId: 'brief', to: 'nouveau statut', userId: 'personne',
  // Données de l’étage: a table or a note reached through a floor that uses another floor's data.
  viaFloorId: 'par l’étage',
};
/** Detail keys whose label depends on the action (`from` / `to` of a department changing floor). */
const ACTION_DETAIL_KEYS = {
  'dept.move': {
    from: 'ancien étage', to: 'nouvel étage', agents: 'agents', routines: 'routines', triggers: 'déclencheurs',
    tasks: 'tâches', grants: 'accès mis à jour', extended: 'droit ajouté (tout l’ancien étage)',
    gained: 'accès nouveau (tout le nouvel étage)', lost: 'accès retiré ou réduit', extendFloorGrants: 'droits sur tout l’ancien étage étendus',
  },
};
const KIND_VALUES = { read: 'lecture', write: 'écriture', null: 'automatique', admin: 'clé administrateur', personal: 'clé personnelle' };
/** `kind` of an access key (mcp.*) is its type, not a tool's classement. */
const KEY_KINDS = new Set(['admin', 'personal']);
/** French wording of a few enumerated values, by detail key. */
const ENUM_VALUES = {
  role: { admin: 'administrateur', member: 'membre' },
  level: { view: 'voir', operate: 'utiliser', manage: 'gérer' },
  autonomy: { approve: 'je valide avant d’agir', auto: 'agit seul' },
  // Why the server revoked a personal MCP key; why a Google Drive's connection failed (Google's own
  // error, e.g. access_denied, shows as it is).
  reason: {
    user_disabled: 'compte désactivé', user_deleted: 'compte supprimé', role_changed: 'devenu administrateur',
    access_denied: 'refusée dans Google', code: 'réponse de Google sans code', app: 'application Google absente ou changée',
    exchange: 'code refusé par Google', refresh_token: 'pas d’accès durable', scope: 'accès non accordé',
    drive: 'compte Google illisible', role: 'ne peut plus gérer ce connecteur', account: 'autre compte Google refusé (fichiers du bureau)',
  },
  // A Google Drive's access.
  mode: { limited: 'limité à un dossier', full: 'tout le Drive' },
  // The state of a task after an approval (task.approve).
  next: {
    queued: 'en attente', running: 'en cours', executing: 'exécution', awaiting_approval: 'à valider', done: 'faite',
    failed: 'échec', scheduled: 'programmée',
  },
};

/** The label of a detail key (`kind` of an access key reads « type »; some keys by action). */
function detailLabel(k, v, action) {
  const byAction = Object.prototype.hasOwnProperty.call(ACTION_DETAIL_KEYS, action) ? ACTION_DETAIL_KEYS[action] : null;
  if (byAction && Object.prototype.hasOwnProperty.call(byAction, k)) return byAction[k];
  if (k === 'kind' && KEY_KINDS.has(v)) return 'type';
  return Object.prototype.hasOwnProperty.call(DETAIL_KEYS, k) ? DETAIL_KEYS[k] : k;
}

function cap(s) {
  const v = String(s || '');
  return v ? v[0].toUpperCase() + v.slice(1) : v;
}

/**
 * French label of an audit action ("user.create" → "Utilisateur créé"; unknown ones →
 * "Routine · lancement immédiat" or a cleaned-up version of the raw action).
 * @param {string} action
 */
export function auditActionLabel(action) {
  const a = String(action ?? '').trim();
  if (!a) return '—';
  if (Object.prototype.hasOwnProperty.call(EXACT, a)) return EXACT[a];
  const parts = a.split('.');
  const domainKey = parts[0];
  const verbKey = parts.slice(1).join('_');
  const domain = Object.prototype.hasOwnProperty.call(DOMAINS, domainKey) ? DOMAINS[domainKey] : cap(domainKey.replace(/[_-]+/g, ' '));
  if (!verbKey) return domain;
  const verb = Object.prototype.hasOwnProperty.call(VERBS, verbKey) ? VERBS[verbKey]
    : Object.prototype.hasOwnProperty.call(VERBS, parts[parts.length - 1]) ? VERBS[parts[parts.length - 1]]
      : verbKey.replace(/[_.-]+/g, ' ');
  return `${domain} · ${verb}`;
}

/**
 * Badge tone of an action: danger | amber | success | neutral. A failure reported in the details
 * (`ok: false`, e.g. an mcp.call refused) is danger whatever the action.
 * @param {string} action
 * @param {object|null} [details]
 */
export function auditTone(action, details = null) {
  const a = String(action ?? '');
  if (details && typeof details === 'object' && details.ok === false) return 'danger';
  if (DANGER_RE.test(a)) return 'danger';
  if (AMBER_RE.test(a)) return 'amber';
  if (SUCCESS_RE.test(a)) return 'success';
  return 'neutral';
}

function valueText(key, v) {
  if (v === null || v === undefined) return key === 'kind' ? KIND_VALUES.null : '—';
  // « Connecteurs autorisés » {mode, keys}: « tous sauf GitHub », « seulement ClickUp ».
  if (key === 'connectorPolicy' && typeof v === 'object' && !Array.isArray(v)) return policySummary(v);
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    if (key === 'kind' && KIND_VALUES[v]) return KIND_VALUES[v];
    const map = Object.prototype.hasOwnProperty.call(ENUM_VALUES, key) ? ENUM_VALUES[key] : null;
    return map && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : v;
  }
  if (Array.isArray(v)) {
    if (v.every((x) => x === null || ['string', 'number', 'boolean'].includes(typeof x))) return v.join(', ');
    // People and their access (dept.move: extended, gained, lost): « bob (voir → gérer) ».
    if (v.every((x) => x && typeof x === 'object' && typeof x.username === 'string')) {
      const word = (level) => (level == null ? 'aucun accès'
        : Object.prototype.hasOwnProperty.call(ENUM_VALUES.level, level) ? ENUM_VALUES.level[level] : String(level));
      return v.map((x) => `${x.username} (${x.before && x.before !== x.level ? `${word(x.before)} → ` : ''}${word(x.level)})`).join(', ');
    }
    return `${v.length}`;
  }
  try { return JSON.stringify(v); } catch { return '…'; }
}

/**
 * Compact, single-line description of audit details: "identifiant : bob · rôle : membre".
 * @param {object|string|null} details
 * @param {number} [max] maximum length (ellipsis beyond)
 * @param {string} [action] the entry's action (a few keys read differently: `dept.move`)
 */
export function formatAuditDetails(details, max = 180, action = '') {
  if (details === null || details === undefined || details === '') return '';
  let s;
  if (typeof details !== 'object') s = String(details);
  else if (Array.isArray(details)) s = valueText('', details);
  else {
    s = Object.entries(details)
      .filter(([k, v]) => !SECRET_KEY_RE.test(k) && v !== undefined)
      // The member of a personal key by name; his id only when the name is missing.
      .filter(([k]) => !(k === 'for' && details.forName))
      .map(([k, v]) => `${detailLabel(k, v, action)} : ${valueText(k, v)}`)
      .join(' · ');
  }
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…` : s;
}

function fold(s) {
  return String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** The user key of an entry for filters: the username, or '' for the system. */
export function auditUserKey(entry) {
  return entry?.username ? String(entry.username) : '';
}

/**
 * Filters entries.
 * @param {object[]} entries
 * @param {{action?: string, user?: string|null, q?: string}} filters `user`: a username, '' for
 *   the system, null/undefined for everyone
 */
export function filterAudit(entries, { action = '', user = null, q = '' } = {}) {
  const needle = fold(q).trim();
  return (Array.isArray(entries) ? entries : []).filter((e) => {
    if (!e || typeof e !== 'object') return false;
    if (action && e.action !== action) return false;
    if (user !== null && user !== undefined && auditUserKey(e) !== user) return false;
    if (needle) {
      const hay = fold(`${e.action} ${auditActionLabel(e.action)} ${e.username || ''} ${e.target || ''} ${formatAuditDetails(e.details, 2000, e.action)}`);
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/**
 * Filter choices present in the entries, with counts: actions sorted by label, users by name
 * (the system — entries without a user — listed as value '').
 * @returns {{actions: Array<{value, label, count}>, users: Array<{value, label, count}>}}
 */
export function auditFacets(entries) {
  const actions = new Map();
  const users = new Map();
  for (const e of Array.isArray(entries) ? entries : []) {
    if (!e || typeof e !== 'object') continue;
    const a = String(e.action || '');
    if (a) actions.set(a, (actions.get(a) || 0) + 1);
    const u = auditUserKey(e);
    users.set(u, (users.get(u) || 0) + 1);
  }
  return {
    actions: [...actions].map(([value, count]) => ({ value, label: auditActionLabel(value), count }))
      .sort((x, y) => x.label.localeCompare(y.label, 'fr')),
    users: [...users].map(([value, count]) => ({ value, label: value || t('adm.audit.system'), count }))
      .sort((x, y) => Number(x.value === '') - Number(y.value === '') || x.label.localeCompare(y.label, 'fr')),
  };
}
